import type {
    CachedAttachmentMetadata,
    FamilyAttachmentMetadataStore
} from './attachment-metadata-store.js'

export type CachedAttachment = {
    data: Blob
    metadata: CachedAttachmentMetadata | undefined
}

export interface FamilyAttachmentStore {
    delete(url: string): Promise<void>
    get(url: string): Promise<CachedAttachment | undefined>
    getMetadata(url: string): Promise<CachedAttachmentMetadata | undefined>
    put(url: string, data: Blob, cachedAt: number): Promise<void>
    recordValidation(url: string, validatedAt: number): Promise<void>
}

export interface AttachmentCache {
    delete(request: RequestInfo | URL): Promise<boolean>
    match(request: RequestInfo | URL): Promise<Response | undefined>
    put(request: RequestInfo | URL, response: Response): Promise<void>
}

export interface AttachmentCacheStorage {
    open(cacheName: string): Promise<AttachmentCache>
}

export type FamilyAttachmentStoreAdapterOptions = {
    cacheStorage: AttachmentCacheStorage
    metadataStore?: FamilyAttachmentMetadataStore
    personKey: string
}

const ATTACHMENT_CACHE_VERSION = 1

export class FamilyAttachmentStoreAdapter implements FamilyAttachmentStore {
    readonly #cacheName: string
    readonly #cacheStorage: AttachmentCacheStorage
    readonly #metadataStore: FamilyAttachmentMetadataStore | undefined

    public constructor(options: FamilyAttachmentStoreAdapterOptions) {
        this.#cacheName = `family-attachments-v${ATTACHMENT_CACHE_VERSION}-${encodeURIComponent(options.personKey)}`
        this.#cacheStorage = options.cacheStorage
        this.#metadataStore = options.metadataStore
    }

    public async delete(url: string): Promise<void> {
        let failure: unknown

        try {
            const cache = await this.#cacheStorage.open(this.#cacheName)
            await cache.delete(url)
        } catch (error) {
            failure = error
        }

        try {
            await this.#metadataStore?.remove(url)
        } catch (error) {
            failure ??= error
        }

        if (failure) {
            throw failure
        }
    }

    public async get(url: string): Promise<CachedAttachment | undefined> {
        const cache = await this.#cacheStorage.open(this.#cacheName)
        const response = await cache.match(url)

        if (!response) {
            try {
                await this.#metadataStore?.remove(url)
            } catch {
                // Missing bytes remain authoritative; metadata cleanup is best effort.
            }
            return undefined
        }

        let metadata: CachedAttachmentMetadata | undefined
        try {
            metadata = await this.getMetadata(url)
        } catch {
            // CacheStorage is the data authority. Metadata failure must not hide usable offline data.
        }

        return { data: await response.blob(), metadata }
    }

    public async getMetadata(url: string): Promise<CachedAttachmentMetadata | undefined> {
        return (await this.#metadataStore?.get(url)) ?? undefined
    }

    public async put(url: string, data: Blob, cachedAt: number): Promise<void> {
        const cache = await this.#cacheStorage.open(this.#cacheName)

        await cache.put(
            url,
            new Response(data, {
                headers: data.type ? { 'Content-Type': data.type } : undefined
            })
        )

        // Keep validation metadata separate so CacheStorage contains only downloaded response data.
        await this.#metadataStore?.put({
            url,
            cachedAt,
            lastValidatedAt: cachedAt,
            size: data.size
        })
    }

    public async recordValidation(url: string, validatedAt: number): Promise<void> {
        if (!this.#metadataStore) {
            return
        }

        const existing = await this.#metadataStore.get(url)
        const cache = await this.#cacheStorage.open(this.#cacheName)
        const response = existing ? undefined : await cache.match(url)
        if (!existing && !response) {
            return
        }
        const size = existing?.size ?? (response ? (await response.blob()).size : undefined)

        await this.#metadataStore.put({
            url,
            cachedAt: existing?.cachedAt ?? validatedAt,
            lastValidatedAt: validatedAt,
            ...(typeof size === 'number' ? { size } : {})
        })
    }
}
